import { Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';
import useLiveTOTP from '../lib/useLiveTOTP';

/**
 * LoginPasswordWidget — shows a SINGLE combined login password = staticPassword + liveTOTP.
 * Tata-Neu style flows where the user has to type "PasswordOTP" together as one string.
 *
 * Click the widget → copies the combined value to clipboard.
 *
 * Props:
 *   password (string)  — the static account password (required)
 *   secret   (string)  — Base32 TOTP secret (required for the OTP suffix)
 *   label    (string)  — toast label, default "Login"
 *   compact  (boolean) — when true renders smaller chip style
 */
export default function LoginPasswordWidget({ password, secret, label = 'Login Password', compact = false, testId = 'login-password' }) {
  const { code, remaining } = useLiveTOTP(secret);
  const combined = (password || '') + (code || '');

  const handleCopy = () => {
    if (!combined) {
      toast.error('Password ya OTP missing hai');
      return;
    }
    navigator.clipboard.writeText(combined);
    toast.success(`${label} copied: ${'●'.repeat(Math.max((password || '').length, 4))}${code}`);
  };

  if (!password && !secret) return null;
  const ringColor = remaining <= 5 ? 'border-red-500 animate-pulse' : remaining <= 10 ? 'border-amber-500' : 'border-emerald-500';
  // Mask shows actual password length (capped at 12 to avoid overflow), so
  // distinct widgets (e.g., 6-digit PIN vs 9-char alphanumeric) look different.
  const masked = `${'●'.repeat(Math.min((password || '').length, 12))}${code || '------'}`;

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={!combined}
      className={`group inline-flex items-center gap-2 ${
        compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-sm'
      } border-2 border-violet-300 rounded font-mono tracking-wider hover:bg-violet-50 disabled:opacity-40 transition-all`}
      title={`Click to copy login password (password + live OTP). Refreshes in ${remaining}s.`}
      data-testid={testId}
    >
      <div className={`shrink-0 ${compact ? 'w-5 h-5' : 'w-7 h-7'} rounded-full border-4 ${ringColor} flex items-center justify-center text-[9px] font-bold`}>
        {remaining || '–'}
      </div>
      <span className="font-bold text-violet-700">{masked}</span>
      <Copy size={compact ? 11 : 14} className="text-violet-400 group-hover:text-violet-600" />
    </button>
  );
}
